<ng-container *ngIf="isV2; else legacyPlanCard">
  <div class="plan-card-wrapper" *ngIf="!isEnterprisePlan; else enterprisePlanCard">
    <!-- Popular badge -->
    <ng-container *ngTemplateOutlet="popularBadge"></ng-container>

    <div class="content-wrapper" [ngClass]="{'with-popular-badge': showPopularBadge }">
      <!-- Plan header -->
      <ng-container *ngTemplateOutlet="planHeader; context: {
        title: plan.nameToDisplay | translate,
        description: planDescription,
        showStatusBadge: isCurrentPlan && !hideStatusBadge
      }"></ng-container>

      <!-- Price section -->
      <div class="price-section-wrapper">
        <app-price-section
          [plan]="plan"
          [hideRecurrenceMessage]="true"
          [payRecurrence]="payRecurrence"
          [userCount]="userCount"
          [simpleRecurrenceLabel]="simpleRecurrenceLabel"
          [inlineLabel]="shouldShowInlineRecurrenceLabel">
        </app-price-section>
        <span
          class="recurrence-description"
          *ngIf="simpleRecurrenceLabel && payRecurrence === 'year' && !hideBilledRecurrenceDescription"
        >
          {{ 'billing.billedRecurrence' | translate: { amount: plan.paymentModel.priceMonthly } }}
        </span>
      </div>

      <!-- Action buttons -->
      <ng-container *ngIf="!hideActionButtons">
        <ng-container *ngTemplateOutlet="choosePlanButtons"></ng-container>
      </ng-container>

      <!-- Features -->
      <ng-container *ngTemplateOutlet="featuresSection"></ng-container>
    </div>
  </div>
</ng-container>

<!-- Enterprise Plan Template -->
<ng-template #enterprisePlanCard>
  <div class="plan-card-wrapper">
    <!-- Popular badge -->
    <ng-container *ngTemplateOutlet="popularBadge"></ng-container>

    <div class="content-wrapper" [ngClass]="{'with-popular-badge': showPopularBadge }">
      <!-- Enterprise header -->
      <ng-container *ngTemplateOutlet="planHeader; context: {
        title: 'billing.enterprisePlan' | translate,
        description: 'billing.enterprisePlanDescription' | translate,
        showStatusBadge: false
      }"></ng-container>

      <!-- Enterprise pricing -->
      <div class="price-wrapper">
        <div class="price-container">
          <span class="price-whole">{{ 'billing.customPlan' | translate }}</span>
        </div>
        <div class="billed-messages-container">
          <span class="recurrence-description">{{ 'billing.enterpriseBilledDescription' | translate }}</span>
        </div>
      </div>

      <!-- Contact sales button -->
      <app-contact-sales
        [buttonText]="'common.contactSales' | translate"
        [buttonClass]="'contact-sales-button'"
        [color]="'primary'"
        [contactSalesFlowId]="'cs-flow-id-at-billing-page'"
        [eventName]="'Contact Sales'"
        [location]="'Billing Details'"
        [trackClick]="{
          page: 'Clicked Contact Sales on Billing Details',
          trialstartdate: currentPlan?.subscription?.trialStartDate,
          trialenddate: currentPlan?.subscription?.trialEndDate
        }">
      </app-contact-sales>

      <!-- Enterprise features -->
        <div class="features-wrapper">
          <h4 class="typography-h6">{{ 'billing.enterpriseFeatureListTitle' | translate }}</h4>
          <ul id="features" class="features-list">
            <li *ngFor="let feature of enterpriseFeatures" class="typography-body1">{{ feature }}</li>
          </ul>
        </div>
    </div>
  </div>
</ng-template>

<!-- Legacy Plan Template -->
<ng-template #legacyPlanCard>
  <div class="header-wrapper">
    <ng-container *ngTemplateOutlet="legacyHeaderContent"></ng-container>
  </div>

  <ng-container *ngTemplateOutlet="priceSection"></ng-container>
  <ng-container *ngIf="!hideActionButtons">
    <ng-container *ngTemplateOutlet="choosePlanButtons"></ng-container>
  </ng-container>
  <ng-container *ngTemplateOutlet="featuresSection"></ng-container>
</ng-template>

<!-- Reusable Templates -->

<!-- Popular Badge Template -->
<ng-template #popularBadge>
  <div *ngIf="badgeInfo$ | async as badge" [class]="badge.className">
    <span>{{ badge.translationKey | translate | capitalize }}</span>
    <mat-icon *ngIf="!hidePopularIcon" svgIcon="popular-icon"></mat-icon>
  </div>
</ng-template>

<!-- Plan Header Template -->
<ng-template #planHeader let-title="title" let-description="description" let-showStatusBadge="showStatusBadge">
  <div class="header-wrapper">
    <div class="header-wrapper-top">
      <h4 class="typography-h4 bold">{{ title }}</h4>
      <app-subscription-status-badge
        *ngIf="showStatusBadge"
        [subscription]="currentPlan.subscription">
      </app-subscription-status-badge>
      <app-subscription-status-badge
        *ngIf="isTrialOfferPlan"
        [subscription]="trialOfferBadge"
        [preferTrialDaysLeft]="true"
        [trialLabelOnly]="true">
      </app-subscription-status-badge>
    </div>
    <p class="typography-body1">{{ description }}</p>
  </div>
</ng-template>

<!-- Choose Plan Buttons Template -->
<ng-template #choosePlanButtons>
  <app-choose-plan-buttons
    [useChoosePlan]="useChoosePlan"
    [currentPlan]="currentPlan"
    [isV2]="isV2"
    [plan]="plan"
    [isCurrentPlan]="isCurrentPlan"
    [payRecurrence]="payRecurrence"
    [nextPaymentDateFormatted]="nextPaymentDateFormatted"
    [determinePlanState]="getDeterminePlanState(plan)"
    [getTrackingData]="getTrackingData.bind(this)"
    (planCardBtnClick)="handlePlanCardBtnClick()">
  </app-choose-plan-buttons>
</ng-template>

<!-- Features Section Template -->
<ng-template #featuresSection>
  <div class="features-wrapper">
    <ng-container *ngTemplateOutlet="featuresList"></ng-container>
  </div>
</ng-template>

<!-- Legacy Header Template -->
<ng-template #legacyHeaderContent>
  <h4 class="typography-h6">{{ plan.nameToDisplay | translate }}</h4>
  <span *ngIf="badgeInfo$ | async as badge" [class]="badge.className">
    {{ badge.translationKey | translate | capitalize }}
  </span>
</ng-template>

<!-- Features List Template -->
<ng-template #featuresList>
  <ng-container *ngIf="isV2; else legacyFeaturesList">
    <h4 *ngIf="showFeaturesListTitle" class="typography-h6">{{ featuresListTitle | translate }}</h4>
    <ng-container *ngTemplateOutlet="featuresListContent; context: {
      cssClass: 'typography-body1',
      showRecurrenceMessage: false
    }"></ng-container>
  </ng-container>

  <ng-template #legacyFeaturesList>
    <ng-container *ngTemplateOutlet="featuresListContent; context: {
      cssClass: 'typography-body2',
      showRecurrenceMessage: true
    }"></ng-container>
  </ng-template>
</ng-template>

<!-- Features List Content Template -->
<ng-template #featuresListContent let-cssClass="cssClass" let-showRecurrenceMessage="showRecurrenceMessage">
  <ul id="features" class="features-list">
    <li *ngIf="showRecurrenceMessage" [class]="cssClass">
      <b class="test-selector">{{ recurrenceMessageEachPlan | translate }}</b>
    </li>
    <li *ngIf="!showRecurrenceMessage && showAllPlanFeaturesPrefix" [class]="cssClass">
      <span class="test-selector">
        {{ 'billing.allPlanFeaturesPrefix' | translate }}
        <b>{{ plan.tierUnderPlan | translate | titlecase }}</b>
        {{ 'billing.allPlanFeaturesSuffix' | translate }}
      </span>
    </li>
    <li *ngFor="let feature of displayedFeatures" [class]="cssClass">
      {{ feature | translate }}
    </li>
  </ul>
</ng-template>

<!-- Price Section Template -->
<ng-template #priceSection>
  <div class="price-wrapper-legacy">
    <span class="typography-h3"
          [matTooltip]="fullPriceForTooltip()"
          [matTooltipDisabled]="!shouldShowTooltip()"
          matTooltipPosition="above">
      {{ currencySymbol }}{{ priceToDisplay() | priceNotation }}
    </span>
    <div class="billed-messages-container">
      <span class="recurrence-label">{{ 'common.user' | translate }} / {{ 'billing.month' | translate }}</span>
      <span class="recurrence-label">{{ recurrenceMessage | translate }}</span>
    </div>
  </div>
</ng-template>
